import { useBaseSentry } from '@/wabunka-src/common/composables/general/useSentry/useSentry';
import type { ExperienceSectionViewModel } from '@/wabunka-src/features/Experience';
import { fetchEnExperienceHeroImages } from '@/wabunka-src/features/Top/core/data/fetchEnExperienceHeroImages';
import { buildExperientialStayCardList, type EnExperienceImageContent, type ExperientialStayCardListViewModel } from '@/wabunka-src/features/Top/models/experientialStay';

import type { Ref } from 'vue';

/**
 * トップページ Experiential Stays セクションの表示データを組み立てる Composable。Page でのみ使用可能。
 *
 * 商品データ（タイトル / 日程 / 価格 / 場所）はバックエンドの体験セクション `Curated_Stays`（`useTopExperienceSections`
 * が取得済みのセクションを受け取る。`/experienceSections` を二重に叩かない）、画像は microCMS `en-experiences`
 * （pcMainImage / spMainImage / subImages）から体験 slug で突合する。突合できない（画像が無い／体験が無い）
 * カードは非表示。並び順はバックエンドの体験配列順を維持する。
 *
 * 重要: コンテキスト依存 API（`useBaseSentry`）は最初の await より前で呼ぶ。
 * en-experiences の取得は await 後になるため、コンテキスト非依存の {@link fetchEnExperienceHeroImages} で行う
 * （await 後に `useAsyncData` / `useRuntimeConfig` / `inject` を呼ぶと SSR・hydration でクラッシュするため）。
 * 画像は ClientOnly セクションでのみ描画されるため、クライアントでのみ取得する。
 *
 * @param curatedStaysSection `useTopExperienceSections` から受け取る Curated_Stays セクション（リアクティブ）
 */
export const useExperientialStays = async (curatedStaysSection: Ref<ExperienceSectionViewModel | undefined>) => {
  // --- await より前にコンテキスト依存 API を解決しておく ---
  const { captureWabunkaException } = useBaseSentry();

  // 画像は通貨に依存しないため、初回のスラグで一度だけ取得する（スナップショット）。
  // 前提: 通貨切替では価格のみ変わり、セクションの体験集合（slug）は不変。集合が変わりうる運用が生じたら
  //       slug を watch して再取得する設計に拡張すること。
  // セクションは <ClientOnly> でのみ描画されるため、SSR での無駄打ちを避けてクライアントでのみ取得する。
  const slugs = (curatedStaysSection.value?.experiences ?? []).map((e) => e.slug).filter(Boolean);
  let enExperienceImages: EnExperienceImageContent[] = [];
  if (import.meta.client && slugs.length > 0) {
    try {
      enExperienceImages = await fetchEnExperienceHeroImages(slugs);
    } catch (e) {
      captureWabunkaException(e as Error);
    }
  }

  // 価格は通貨で変わるため curatedStaysSection（リアクティブ）から都度再計算。画像はスナップショットで十分。
  const experientialStayList = computed<ExperientialStayCardListViewModel>(() =>
    curatedStaysSection.value ? buildExperientialStayCardList(curatedStaysSection.value.experiences, enExperienceImages) : [],
  );

  return { experientialStayList };
};
